import axios from "axios"
import router from "@/router"
import { ROUTES } from "@/router/paths"
import type { AxiosResponse as Response } from "axios"
import errorHandler from "@/plugins/errorHandler"
import { type App } from "vue"
import {Capacitor} from "@capacitor/core"
import {Browser} from "@capacitor/browser"
import {Device} from "@capacitor/device"

export type AxiosResponse<T = object> = Response<T & { errors?: string[] }>

export interface AxiosPaginationResponse<ItemType> extends AxiosResponse {
  data: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page_url: string
    last_page_url: string
    next_page_url?: string
    prev_page_url?: string
    path: string
    from: number
    to: number
    data: ItemType[]
    errors?: string[]
  }
}


axios.defaults.headers.common = {
  Accept: "application/json",
  "Content-Type": "application/json"
}

function getBaseUrl(forcePhysicalDevice = false): string {
  const configuredUrl = import.meta.env.VITE_API_BASE_URL
  // If a full URL is configured (staging/prod), use it as-is
  if (configuredUrl?.startsWith('http')) return configuredUrl
  // On native platforms with a relative URL, resolve against the local dev server
  if (Capacitor.isNativePlatform() && import.meta.env.VITE_LOCAL_SERVER) {
    const platform = Capacitor.getPlatform()
    const port = import.meta.env.VITE_LOCAL_DEV_PORT || '56342'
    // On physical devices, use VITE_LOCAL_DEV_HOST (Computer's local IP).
    // On emulators/simulators, ignore it: Android uses 10.0.2.2, iOS uses localhost.
    const host = (forcePhysicalDevice && import.meta.env.VITE_LOCAL_DEV_HOST)
      || (platform === 'android' ? '10.0.2.2' : 'localhost')
    return `http://${host}:${port}${configuredUrl || '/api/v1'}`
  }
  return configuredUrl || '/api/v1'
}

// Default to emulator behavior synchronously, then upgrade to physical-device
// host once Device.getInfo() confirms we're on a real device. If detection
// fails for any reason, leave the default in place (current behavior).
axios.defaults.baseURL = getBaseUrl(false)
if (Capacitor.isNativePlatform() && import.meta.env.VITE_LOCAL_DEV_HOST) {
  Device.getInfo()
    .then((info) => {
      if (!info.isVirtual) {
        axios.defaults.baseURL = getBaseUrl(true)
      }
    })
    .catch(() => { /* keep emulator default on failure */ })
}
axios.interceptors.request.use((config) => {

  if (localStorage.getItem("token") && !config.url?.includes('s3.amazonaws.com')) {
    config.headers["Authorization"] = "bearer " + localStorage.getItem("token")
  }

  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = 'multipart/form-data'
  }

  config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone

  return config
})

axios.interceptors.response.use(
  async function(response) {
    const redirect = response.headers['x-vue-redirect']
    if (redirect) {
      if (redirect.includes("http")) {
        if (Capacitor.isNativePlatform()) {
          // Flag that we're opening an OAuth browser, so on Android cold-start
          // we can navigate back to settings instead of showing PIN select.
          localStorage.setItem('oauth_in_progress', '1')
          await Browser.open({ url: redirect })
        } else {
          window.location.href = redirect
        }
      } else {
        await router.push(response.headers['x-vue-redirect'])
        return Promise.reject(response)
      }
    }
    return response
  },
  async function({ response, message }) {
    if (!response) {
      return Promise.reject({ status: 500, data: { message } })
    }
    const redirect = response.headers['X-vue-redirect']
    if (redirect) {
      if (redirect.includes("http")) {
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url: redirect })
        } else {
          window.location.href = redirect
        }
      } else {
        await router.push(response.headers['X-vue-redirect'])
        return Promise.reject(response)
      }
    }
    // A suspended member gets 402 seat_suspended on anything gated. Send them
    // to the page that explains it rather than letting the call fail silently.
    
    if (response.status === 402 && response.data?.code === 'upgrade_required') {
      // Imported here rather than at the top: utils/upgrade reaches back to
      // auth, which reaches back to this file.
      const {promptUpgrade} = await import("@/utils/upgrade")
      promptUpgrade()
      return Promise.reject(response)
    }

    if (response.status === 402 && response.data?.code === 'seat_suspended') {
      if (router.currentRoute.value.name !== ROUTES.SUSPENDED) {
        await router.push({ name: ROUTES.SUSPENDED })
      }
      return Promise.reject(response)
    }

    if (
      response.status === 401 ||
      response.data.message === "Authentication is required to continue"
    ) {
      // if (router.currentRoute.value.name !== ROUTES.LOGIN) {
      //   const userStore = useUserStore()
      //   userStore.logout().then(() => {
      //     router
      //       .push({ name: ROUTES.LOGIN, query: { to: router.currentRoute.value.fullPath } })
      //       .catch((e: Error) => e)
      //   })
      // }
    }
    return Promise.reject(response)
  }
)

axios.download = async function <T = any, R = AxiosResponse<T>>(url: string, params = {}, method = "get", providedFilename?: string): Promise<R> {
  const response = await this.request({
    url,
    method,
    params: method === "get" ? params : null,
    data: method !== "get" ? params : null,
    responseType: "blob",
  }).catch(e => e)

  if (response.status > 299) {
    const jsonData = await new Response(response.data).json()
    if (errorHandler(response.status, jsonData.message)) return response
  }

  // Get File Name - use provided filename if available, otherwise try to extract from headers
  let filename = providedFilename || "file"
  if (!providedFilename && response.headers["content-disposition"]) {
    const headerLine = response.headers["content-disposition"]
    const filenameMatch = headerLine.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (filenameMatch) {
      filename = filenameMatch[1].replace(/^["']|["']$/g, '') // Remove surrounding quotes
    }
  }

      // Download handling for iOS/iPad compatibility
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1)

      if (isIOS) {
        // For iOS, use in-app browser to stay within the app
        try {
          // Extract file ID from URL like '/files/123/original' and construct proper download endpoint
          const fileIdMatch = url.match(/\/files\/(\d+)\//)
          if (fileIdMatch) {
            const fileId = fileIdMatch[1]
            // Use full URL for Browser.open() - it needs absolute URL
            // If VITE_API_BASE_URL is relative (e.g., /api/v1), make it absolute
            const apiBase = import.meta.env.VITE_API_BASE_URL || '/api/v1'
            const baseUrl = apiBase.startsWith('http') ? apiBase : `${window.location.origin}${apiBase}`
            const downloadUrl = `${baseUrl}/files/${fileId}/download`
            if (Capacitor.isNativePlatform()) {
              await Browser.open({ url: downloadUrl })
            } else {
              window.open(downloadUrl, '_blank')
            }
            return response
          }
        } catch (e) {
          // Show download error to user via app store
          const { useAppStore } = await import('@/stores/app')
          useAppStore().addError(`iOS download failed: ${e instanceof Error ? e.message : 'Unknown error'}`)
          return response
        }
      }

  // Fallback: Use blob URL method (works on most browsers)
  try {
    const href = URL.createObjectURL(response.data)
    const link = document.createElement("a")
    link.href = href
    link.setAttribute("download", filename)
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  } catch (e) {
    // Blob download failed, try direct download
    // Cursor: Extract file ID and construct proper download endpoint for fallback
    const fileIdMatch = url.match(/\/files\/(\d+)\//)
    if (fileIdMatch) {
      const fileId = fileIdMatch[1]
      // Use full URL for Browser.open() - it needs absolute URL
      // If VITE_API_BASE_URL is relative (e.g., /api/v1), make it absolute
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api/v1'
      const baseUrl = apiBase.startsWith('http') ? apiBase : `${window.location.origin}${apiBase}`
      const downloadUrl = `${baseUrl}/files/${fileId}/download`
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: downloadUrl })
      } else {
        window.open(downloadUrl, '_blank')
      }
    } else {
      // If we can't extract file ID, use original URL (make it absolute if needed)
      const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: fullUrl })
      } else {
        window.open(fullUrl, '_blank')
      }
    }
  }

  return response
}

export default {
  install(app: App) {
    app.config.globalProperties.$http = axios
  }
}
